import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { Input, Select } from '../../../ui/Field/Field';
import ProgressBar from '../../../ui/Progress/ProgressBar';
import { finesService, errorMessage } from '../../../services/finesService';

// "Export paid fines" (owner, 19 Oct 2026): the fines paid at RTA between two payment dates - or on chosen days only
// ("يوم 3 و5 و10 اكتوبر ومش عايز معاهم يوم 6") - paid by anyone, the company only or the customer only, as an Excel
// file: Summary, Details (ticket, date, time, plate, source, amount, contract, customer, phone, branch, payment date,
// contract type, notes) and Ejar errors. A fine paid with a discount is billed in full: the company's gain.

const pad = (n) => String(n).padStart(2, '0');
const dayText = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** The first day of this month and today (the browser's day: staff work in Dubai). */
export const defaultRange = (now = new Date()) => ({ from: dayText(new Date(now.getFullYear(), now.getMonth(), 1)), to: dayText(now) });
const MAX_DAYS = 62;      // the engine's MAX_PICKED_DAYS
/** "2026-10-03" -> "3 Oct 2026" */
const dayLabel = (d) => {
    const [y, m, dd] = d.split('-').map(Number);
    return `${dd} ${new Date(y, m - 1, dd).toLocaleString('en-GB', { month: 'short' })} ${y}`;
};

// The branch is the window's own choice, All by default - never the Fines page's filter taken along unseen (owner,
// 19 Oct 2026: 1 Sep exported empty, the page's Branch filter "Unassigned_Fines" had gone with it)
export default function PaidFinesExportModal({ onClose, branches = [] }) {
    const [mode, setMode] = useState('range');        // 'range' | 'days'
    const [range, setRange] = useState(defaultRange);
    const [days, setDays] = useState([]);              // the specific payment days, sorted
    const [dayDraft, setDayDraft] = useState('');
    const [paidBy, setPaidBy] = useState('');          // '' all | 'company' | 'customer'
    const [branch, setBranch] = useState('');          // '' all branches
    const [job, setJob] = useState(null);
    const [error, setError] = useState(null);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const running = job?.status === 'running';
    const problem = mode === 'range'
        ? ((!range.from || !range.to || range.from > range.to) ? 'Choose a From date on or before the To date.' : '')
        : (!days.length && !dayDraft ? 'Add at least one day.' : '');
    // a day picked but not added yet counts too (it would be easy to miss "Add day")
    const daysToSend = () => (dayDraft && !days.includes(dayDraft) ? [...days, dayDraft].sort() : days);

    const addDay = () => {
        if (!dayDraft || days.includes(dayDraft) || days.length >= MAX_DAYS) return;
        setDays((d) => [...d, dayDraft].sort());
        setDayDraft('');
    };

    const start = async () => {
        setError(null);
        setJob({ status: 'running', stage: 'starting', done: 0, total: 0 });
        const params = mode === 'range'
            ? { date_from: range.from, date_to: range.to, branch, paid_by: paidBy }
            : { days: daysToSend(), branch, paid_by: paidBy };
        try {
            const done = await finesService.paidFines(params, (j) => { if (alive.current) setJob({ ...j, status: 'running' }); });
            if (alive.current) setJob(done);
        } catch (e) {
            if (alive.current) { setError(errorMessage(e)); setJob(null); }
        }
    };

    const state = running ? 'running' : job?.status === 'done' ? 'done' : 'stopped';
    return (
        <Dialog open onClose={onClose} size="md" title="Export paid fines"
                description="The fines paid at RTA between two payment dates or on chosen days, with a Summary, the Details and the Ejar errors.">
            <div className="flex flex-wrap items-end justify-center gap-3 mb-3">
                <label className="flex flex-col text-sm text-gray-700">Payment dates
                    <Select aria-label="Payment dates" value={mode} disabled={running} onChange={(e) => setMode(e.target.value)}>
                        <option value="range">From - To</option>
                        <option value="days">Specific days</option>
                    </Select>
                </label>
                <label className="flex flex-col text-sm text-gray-700">Branch
                    <Select aria-label="Branch" value={branch} disabled={running} onChange={(e) => setBranch(e.target.value)}>
                        <option value="">All branches</option>
                        {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                    </Select>
                </label>
                <label className="flex flex-col text-sm text-gray-700">Paid by
                    <Select aria-label="Paid by" value={paidBy} disabled={running} onChange={(e) => setPaidBy(e.target.value)}>
                        <option value="">All</option>
                        <option value="company">Company only</option>
                        <option value="customer">Customer only</option>
                    </Select>
                </label>
            </div>
            {mode === 'range' ? (
                <div className="flex flex-wrap items-end justify-center gap-3 mb-4">
                    <label className="flex flex-col text-sm text-gray-700">Paid from
                        <Input type="date" aria-label="Paid from" value={range.from} disabled={running}
                               onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
                    </label>
                    <label className="flex flex-col text-sm text-gray-700">Paid to
                        <Input type="date" aria-label="Paid to" value={range.to} disabled={running}
                               onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
                    </label>
                </div>
            ) : (
                <div className="mb-4">
                    <div className="flex flex-wrap items-end justify-center gap-2">
                        <label className="flex flex-col text-sm text-gray-700">Day
                            <Input type="date" aria-label="Day to add" value={dayDraft} disabled={running}
                                   onChange={(e) => setDayDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addDay()} />
                        </label>
                        <Button size="sm" variant="secondary" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />} onClick={addDay}
                                disabled={running || !dayDraft || days.includes(dayDraft) || days.length >= MAX_DAYS}>Add day</Button>
                    </div>
                    <div className="mt-3 flex flex-wrap justify-center gap-2" aria-label="Chosen days">
                        {days.map((d) => (
                            <span key={d} className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-purple-50 px-3 py-1 text-sm font-medium text-purple-900">
                                {dayLabel(d)}
                                <button type="button" aria-label={`Remove ${dayLabel(d)}`} disabled={running}
                                        onClick={() => setDays((x) => x.filter((y) => y !== d))}>
                                    <Icon glyph={ICONS.close} size="sm" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {problem && <div role="alert" className="mb-3 text-center text-sm text-red-700">{problem}</div>}
            {error && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {job && (
                <div className="mb-3">
                    <ProgressBar done={job.done || 0} total={job.total || 0} unit="fines" state={state}
                                 label={running ? 'Preparing the file' : 'File downloaded'}
                                 note={running ? job.stage : `${job.rows ?? 0} fine(s) · ${job.errors ?? 0} Ejar error(s)`} />
                </div>
            )}
            <div className="flex justify-center gap-2">
                <Button size="sm" variant="secondary" onClick={onClose}>{running ? 'Close (it keeps going)' : 'Close'}</Button>
                <Button size="sm" variant="primary" leadingIcon={<Icon glyph={ICONS.spreadsheet} size="sm" />}
                        onClick={start} disabled={!!problem || running} isLoading={running}>
                    {job?.status === 'done' ? 'Export again' : 'Export'}
                </Button>
            </div>
        </Dialog>
    );
}
